import { Dialog } from "@mui/material";
import { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { format } from "date-fns";
import { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import AlertDialog from "../../components/shared/AlertDialog";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import StatusLabel from "./StatusLabel";
import { Box, ButtonPrimary, HStack, VStack } from "../../components/utils";
import { AddOrEditPoolTransactionWrapper } from "../allPlans/AddOrEditPoolTransaction";
import {
  useDeletePoolTransaction,
  usePoolTransactions,
} from "../../queries/esopPlan";
import { useError } from "../../store/errorStore";
import { usePoolTransactionDialog } from "../../store/useDialogStore";
import { PoolTransaction } from "../../types/Grant";
import { getCurrencyType } from "../../utils/currencyFormatter";
import { determineUserAccessToResource } from "../../utils/auth";
import { usePermissionStore } from "../../store/permissionStore";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";

function PoolTransactionsTable() {
  const { data: _transactions } = usePoolTransactions();
  const transactions =
    _transactions?.sort(
      (a, b) =>
        new Date(b.transactionDate).getTime() -
        new Date(a.transactionDate).getTime()
    ) ?? [];
  const { mutate: deleteTransaction, isLoading: deleting } =
    useDeletePoolTransaction();
  const { setState: setDialog, state: dialogState } =
    usePoolTransactionDialog();
  const { permission } = usePermissionStore();
  const errorMessage = useError();
  const currency = getCurrencyType();
  const gridApi = useRef<GridApi | null>(null);
  const gridRef = useRef<AgGridReact<PoolTransaction>>(null);

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    transaction?: PoolTransaction;
  }>({ open: false });

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline text-xs font-medium text-gray-dark leading-5",
      columnsMenuParams: { suppressColumnFilter: true },
      filterParams: { buttons: ["reset"], maxNumConditions: 5 },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const columnDefs: ColDef[] = [
    {
      headerName: "TRANSACTION DATE",
      field: "transactionDate",
      filter: "agDateColumnFilter",
      valueFormatter: (params) =>
        params.value ? format(new Date(params.value), "dd MMM yyyy") : "",
      menuTabs: ["filterMenuTab"],
      sortable: true,
      autoHeight: true,
      wrapText: true,
      minWidth: 180,
    },
    {
      headerName: "TYPE",
      field: "transactionType",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      sortable: true,
      autoHeight: true,
      wrapText: true,
      minWidth: 140,
      cellStyle: { paddingTop: "15px" },
      filterValueGetter: (params) =>
        params.getValue("transactionType")?.props?.state,
      cellRendererParams: ({ value }: { value: any }) =>
        value ? value.props : null,
      cellRenderer: StatusLabel,
    },
    {
      headerName: "NO. OF OPTIONS",
      field: "noOfOptions",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value != null ? params.value.toLocaleString(currency) : "",
      menuTabs: ["filterMenuTab"],
      sortable: true,
      autoHeight: true,
      wrapText: true,
      minWidth: 160,
    },
    {
      headerName: "NOTES",
      field: "notes",
      filter: "agTextColumnFilter",
      menuTabs: ["filterMenuTab"],
      sortable: true,
      autoHeight: true,
      wrapText: true,
    },
    {
      headerName: "",
      field: "actions",
      pinned: "right",
      hide: false,
      width: 80,
      maxWidth: 80,
      filter: false,
      colId: "action-column",
      suppressNavigable: true,
      suppressColumnsToolPanel: true,
      resizable: false,
      sortable: false,
      menuTabs: [],
      cellRendererParams: ({ value }: { value: any }) =>
        value ? value.props : null,
      cellRenderer: CTADropdown,
    },
  ];

  const rowData = useMemo(
    () =>
      transactions.map((t) => ({
        transactionDate: t.transactionDate,
        transactionType: (
          <StatusLabel state={t.transactionType} _className="text-[12px] p-1" />
        ),
        noOfOptions: t.noOfOptions,
        notes: t.notes,
        actions: (
          <CTADropdown
            dropdownClassName="xl:right-10 lg:right-8 md:right-2"
            actions={[
              {
                name: "Edit",
                disabled: !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.PoolTransaction,
                  DefaultAction.Edit
                ),
              },
              {
                name: "Delete",
                disabled: !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.PoolTransaction,
                  DefaultAction.Delete
                ),
              },
            ]}
            onAction={(action) => handleAction(t, action)}
          />
        ),
      })),
    [transactions, permission]
  );

  function handleAction(transaction: PoolTransaction, action: Action) {
    if (action.name === "Edit" && !action.disabled) {
      setDialog({ open: true, mode: "Edit", transaction });
    } else if (action.name === "Delete" && !action.disabled) {
      setDeleteDialog({ open: true, transaction });
    }
  }

  function handleDelete() {
    if (!deleteDialog.transaction) return;
    deleteTransaction(deleteDialog.transaction.id, {
      onSuccess: () => {
        toast("Transaction deleted successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setDeleteDialog({ open: false });
      },
      onError: (err: any) => {
        const msg =
          err.response?.data?.reason ||
          err.response?.data?.errorRaw ||
          "Failed to delete transaction";
        errorMessage.setMessage(msg);
        toast(msg, { type: "error", autoClose: 2000 });
        setDeleteDialog({ open: false });
      },
    });
  }

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
  };

  return (
    <VStack className="gap-4">
      <Box className="p-8 bg-white rounded-lg border border-borderColor drop-shadow-box">
        <HStack className="justify-between mb-8">
          <VStack aria-label="toolbar" className="items-start">
            <h4 className="text-lg font-medium text-gray-dark">
              Pool Transactions
            </h4>
            <p className="text-xs font-normal text-gray-light">
              Update the pool transactions history by adding new transactions or
              editing existing ones. Updating transactions here does not alter
              current pool size.
            </p>
          </VStack>
          <HStack>
            <ButtonPrimary
              disabled={
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.PoolTransaction,
                  DefaultAction.Create
                )
              }
              onClick={() => setDialog({ open: true, mode: "Add" })}
            >
              Add Transaction
            </ButtonPrimary>
          </HStack>
        </HStack>

        {transactions.length > 0 ? (
          <HStack className="w-full">
            <Box
              style={{ height: "400px" }}
              className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
            >
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                onGridReady={onGridReady}
                rowClass={
                  "border-t border-dashed cursor-pointer hover:bg-slate-50 "
                }
                defaultColDef={defaultColDef}
                animateRows={true}
                suppressRowTransform={true}
                suppressRowClickSelection
                pagination={true}
                suppressCellFocus={true}
                suppressMenuHide={true}
                suppressCopyRowsToClipboard={true}
                suppressCopySingleCellRanges={true}
              />
            </Box>
          </HStack>
        ) : (
          <p className="text-sm text-gray-400">No transactions found.</p>
        )}
      </Box>

      {/* Add / Edit dialog */}
      <Dialog
        open={dialogState.open}
        onClose={() => setDialog({ open: false })}
        maxWidth="md"
      >
        <AddOrEditPoolTransactionWrapper
          mode={dialogState.mode}
          transaction={dialogState.transaction}
          isFirstTransaction={transactions.length === 0}
          onClose={() => setDialog({ open: false })}
        />
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
        maxWidth="md"
      >
        <AlertDialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false })}
          message="Do you want to delete this transaction?"
          onPrimaryAction={handleDelete}
          onSecondaryAction={() => setDeleteDialog({ open: false })}
          loading={deleting}
        />
      </Dialog>
    </VStack>
  );
}

export default PoolTransactionsTable;
